import { motion } from 'framer-motion';
import { useState } from 'react';

export function Consultation() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
  });
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const REPRESENTATIVE_NUMBER = '1688-1502';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // 유효성 검사
    if (!agreed) {
      setSubmitStatus('error');
      return;
    }

    const trimmedName = formData.name.trim();
    const trimmedPhone = formData.phone.trim();

    if (!trimmedName || !trimmedPhone) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // FormData 객체 생성
      const formBody = new FormData();
      formBody.append('name', trimmedName);
      formBody.append('phone', trimmedPhone);

      // fetch 요청 with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15초 타임아웃

      const response = await fetch('https://readdy.ai/api/form/d64lh5kbd5onm6bapldg', {
        method: 'POST',
        body: formBody,
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '' });
        setAgreed(false);
        
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        console.error('Form submission failed:', response.status, response.statusText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="py-12 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 md:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            관심고객 등록
          </h2>
          <p className="text-base md:text-lg text-gray-300 px-4">
            등록하시면 특별 분양 혜택 및 최신 소식을 가장 먼저 안내해드립니다
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-stretch">
          {/* 관심고객 등록 폼 */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form
              id="interest-registration-form"
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl h-full flex flex-col justify-between"
              noValidate
            >
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">관심고객 등록</h3>
                <p className="text-xs sm:text-sm text-gray-500 mb-6 md:mb-8">간단한 정보만 입력하시면 빠르게 연락드리겠습니다</p>

                <div className="space-y-4 md:space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      이름 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      autoComplete="name"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                      placeholder="이름을 입력하세요"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      연락처 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      autoComplete="tel"
                      inputMode="tel"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all"
                      placeholder="010-0000-0000"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="mt-5 md:mt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-600 cursor-pointer flex-shrink-0"
                      disabled={isSubmitting}
                    />
                    <span className="text-xs text-gray-500 leading-relaxed">
                      개인정보 수집 및 이용에 동의합니다. 수집된 정보는 분양 안내 목적으로만 사용되며, 목적 달성 후 안전하게 파기됩니다.
                    </span>
                  </label>
                </div>
              </div>

              <div className="mt-6 md:mt-8">
                {submitStatus === 'success' && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-green-800 text-center text-xs sm:text-sm font-semibold">
                      관심고객 등록이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-800 text-center text-xs sm:text-sm font-semibold">
                      {!agreed ? '개인정보 수집 및 이용에 동의해주세요.' : '등록 중 오류가 발생했습니다. 다시 시도해주세요.'}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 md:py-4 bg-amber-600 text-white text-base md:text-lg font-bold rounded-xl hover:bg-amber-700 active:bg-amber-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer touch-manipulation"
                >
                  {isSubmitting ? '등록 중...' : '관심고객 등록하기'}
                </button>
              </div>
            </form>
          </motion.div>

          {/* 대표번호 바로 연결 */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl h-full flex flex-col justify-between">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">대표번호 바로 연결</h3>
                <p className="text-xs sm:text-sm text-gray-500 mb-6 md:mb-8">지금 바로 전화하시면 전문 상담사가 친절하게 안내해드립니다</p>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 md:p-8 text-center mb-5 md:mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-amber-600 rounded-full mx-auto mb-4 md:mb-5">
                    <i className="ri-phone-fill text-white text-xl md:text-2xl"></i>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-2 font-medium">대표 상담 전화</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {REPRESENTATIVE_NUMBER}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-amber-600"></i>
                    </div>
                    <span className="text-sm">24시 상담</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-close-line text-amber-600"></i>
                    </div>
                    <span className="text-sm">연중무휴</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-amber-600"></i>
                    </div>
                    <span className="text-sm">모델하우스 방문 상담 가능</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 md:mt-8">
                <a
                  href={`tel:${REPRESENTATIVE_NUMBER}`}
                  className="w-full py-3 md:py-4 bg-gray-900 text-white text-base md:text-lg font-bold rounded-xl hover:bg-gray-800 active:bg-gray-700 transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer touch-manipulation"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-phone-fill text-base md:text-lg"></i>
                  </div>
                  전화 상담 바로 연결
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
